// =====================================================
// EXPORT DATE RANGE MODAL
// Modal reutilizable para seleccionar rango de fechas
// antes de exportar agenda o historias clínicas a Excel
// =====================================================

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Download, Loader2 } from 'lucide-react';

interface ExportDateRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (from: string, to: string) => Promise<void>;
  title: string;
  description?: string;
}

const ExportDateRangeModal: React.FC<ExportDateRangeModalProps> = ({
  isOpen,
  onClose,
  onExport,
  title,
  description
}) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});

  // Calcular posición del overlay para cubrir la viewport visible
  const updatePosition = useCallback(() => {
    setOverlayStyle({
      position: 'absolute',
      top: window.scrollY,
      left: 0,
      width: '100%',
      height: window.innerHeight,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    });
  }, []);

  // Al abrir: bloquear scroll y posicionar overlay
  useEffect(() => {
    if (isOpen) {
      updatePosition();
      document.body.style.overflow = 'hidden';

      // Escuchar resize por si cambia la viewport (teclado virtual, rotación)
      window.addEventListener('resize', updatePosition);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  const handleExport = async () => {
    setError('');

    if (!fromDate || !toDate) {
      setError('Selecciona ambas fechas');
      return;
    }

    if (fromDate > toDate) {
      setError('La fecha de inicio no puede ser mayor que la fecha de fin');
      return;
    }

    try {
      setIsExporting(true);
      await onExport(fromDate, toDate);
      handleClose();
    } catch (err) {
      setError('Error al exportar. Intenta nuevamente.');
      console.error('Error en exportación:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (isExporting) return;
    setFromDate('');
    setToDate('');
    setError('');
    onClose();
  };

  // Presets rápidos
  const setLastMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    setFromDate(firstDay.toISOString().split('T')[0]);
    setToDate(lastDay.toISOString().split('T')[0]);
    setError('');
  };

  const setThisMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    setFromDate(firstDay.toISOString().split('T')[0]);
    setToDate(now.toISOString().split('T')[0]);
    setError('');
  };

  const setLastWeek = () => {
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);
    setFromDate(weekAgo.toISOString().split('T')[0]);
    setToDate(now.toISOString().split('T')[0]);
    setError('');
  };

  if (!isOpen) return null;

  return createPortal(
    <div style={overlayStyle}>
      {/* Backdrop - click para cerrar */}
      <div
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)' }}
        onClick={handleClose}
      />

      {/* Modal card */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto"
        style={{ width: 'calc(100% - 2rem)', maxWidth: '28rem', margin: '0 1rem' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900">{title}</h2>
              {description && (
                <p className="text-xs sm:text-sm text-gray-500">{description}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isExporting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6 space-y-4">
          {/* Presets rápidos */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={setLastWeek}
              disabled={isExporting}
              className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Última semana
            </button>
            <button
              onClick={setThisMonth}
              disabled={isExporting}
              className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Este mes
            </button>
            <button
              onClick={setLastMonth}
              disabled={isExporting}
              className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Mes anterior
            </button>
          </div>

          {/* Fecha desde */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Desde
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setError(''); }}
                disabled={isExporting}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all disabled:opacity-50 disabled:bg-gray-50"
              />
            </div>
          </div>

          {/* Fecha hasta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Hasta
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setError(''); }}
                disabled={isExporting}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all disabled:opacity-50 disabled:bg-gray-50"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 sm:p-6 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
          <button
            onClick={handleClose}
            disabled={isExporting}
            className="px-4 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl transition-colors font-medium disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || !fromDate || !toDate}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exportando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Exportar
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExportDateRangeModal;
